// helpers/whatever.ts
type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'text';
import { AsYouType } from 'libphonenumber-js';
import type { Color } from '@/sanity.types';

type LegacyColorObject = {
  hex?: string;
  rgb?: { r: number; g: number; b: number; a?: number };
  hsl?: { h: number; s: number; l: number; a?: number };
};
type BackgroundColorInput = string | Color | LegacyColorObject | undefined;
import { INVISIBLES, stripInvisibles } from '../constants';
import { Link } from '../interfaces';

export const getLinkUrl = (link?: Link): string => {
  if (!link) return '#';

  // Handle based on link type first to prioritize type-specific fields
  switch (link.type) {
    case 'internal':
      // For internal links, prioritize internalReference.url over link.url
      return link.internalReference?.url || link.url || '#';
    case 'external':
      // For external links, prioritize externalUrl over link.url
      return link.externalUrl || link.url || '#';
    case 'anchor':
      if (!link.anchor) return '#';
      return link.anchor.startsWith('#') ? link.anchor : `#${link.anchor}`;
    default:
      // Fallback: if no type specified, use link.url if available
      return link.url || '#';
  }
};

export const getLinkProps = (link?: Link | string | null) => {
  // Handle null/undefined
  if (!link) return {};

  // Handle string links (treat as external)
  if (typeof link === 'string') {
    return {
      href: link,
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }

  const url = getLinkUrl(link);

  // External links always open in new tab
  if (link.type === 'external') {
    return {
      href: url,
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }

  // For internal links, check isOpenInNewTab flag
  if (link.isOpenInNewTab && link.type !== 'anchor') {
    return {
      href: url,
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }

  // Return standard link props (no target/rel for same-tab navigation)
  return {
    href: url,
  };
};

/**
 * Helper function to determine if a link should open in a new tab
 * External links always open in new tab, regardless of isOpenInNewTab setting
 */
export const shouldOpenInNewTab = (link: any): boolean => {
  if (!link) return false;

  // Check multiple possible field names for external link type
  const isExternal = link.type === 'external' || link.linkType === 'external' || link._type === 'external';

  // External links always open in new tab
  if (isExternal) return true;

  // Otherwise check the explicit flags
  return !!(link.isOpenInNewTab || link.openInNewTab || link.newTab || link.blank);
};

export const isLinkActive = (link?: Link, activePath?: string): boolean => {
  if (!link || !activePath) return false;

  const stripQueryHash = (s: string) => s.split('#')[0].split('?')[0];
  const trimTrailingSlash = (s: string) => (s !== '/' ? s.replace(/\/+$/, '') : '/');
  const normalize = (s: string) => trimTrailingSlash(stripQueryHash(s));

  const targetFromLink = (l: Link): string | undefined => {
    switch (l.type) {
      case 'internal':
        return l.internalReference?.url ?? l.url ?? undefined;
      case 'external':
        return l.externalUrl ?? undefined;
      case 'anchor':
        return l.anchor ? `#${l.anchor}` : undefined;
      default:
        return undefined;
    }
  };

  // Anchor links: consider active if the hash appears in the current path
  if (link.type === 'anchor' && link.anchor) {
    return activePath.includes(`#${link.anchor}`);
  }

  const target = targetFromLink(link);
  if (!target) return false;

  const a = normalize(activePath);
  const t = normalize(target);

  if (a === t) return true;

  // Treat descendants as active (but not root '/')
  if (t !== '/' && a.startsWith(`${t}/`)) return true;

  return false;
};

export const getActivePathFromLink = (link?: Link): string => {
  if (!link) return '';
  if (link.type === 'internal') {
    if (link.internalReference?.url) return link.internalReference.url;
    if (link.url) return link.url;
  }
  if (link.type === 'external') {
    if (link.externalUrl) return link.externalUrl;
    if (link.url) return link.url;
  }
  if (link.type === 'anchor' && link.anchor) return `#${link.anchor}`;
  return '';
};

export const handleNavigationClick = (link?: Link, onActivePathChange?: (newPath: string) => void) => {
  if (!link || !onActivePathChange) return;

  let newPath = '';
  if (link.type === 'internal' && link.internalReference?.url) {
    newPath = link.internalReference?.url;
  } else if (link.type === 'external') {
    if (link.externalUrl) {
      newPath = link.externalUrl;
    } else if (link.url) {
      newPath = link.url;
    }
  } else if (link.type === 'anchor' && link.anchor) {
    newPath = `#${link.anchor}`;
  }

  if (newPath) {
    onActivePathChange(newPath);
  }
};

export const getBackgroundClass = (customBackgroundColor?: BackgroundColorInput): string => {
  if (!customBackgroundColor) return 'bg-white';

  // Handle string colors (new Tailwind class format or legacy hex strings)
  if (typeof customBackgroundColor === 'string') {
    // Strip invisible/stega chars before using the value
    const cleanString = customBackgroundColor.replace(INVISIBLES, '').trim();

    // If it's already a Tailwind class (starts with bg-), return it
    if (cleanString.startsWith('bg-')) {
      return cleanString;
    }

    // Clean the hex color by removing any non-hex characters and invisible characters
    const cleanHex = cleanString.replace(/[^#0-9A-Fa-f]/g, '');

    // If it's a valid hex color, convert to inline style (legacy support)
    if (/^#[0-9A-Fa-f]{3,6}$/.test(cleanHex)) {
      return ''; // Return empty string for legacy hex colors to use inline styles
    }
  }

  // Handle Sanity color picker objects (legacy format)
  if (typeof customBackgroundColor === 'object') {
    // For legacy color picker objects, return empty string to use inline styles
    return '';
  }

  return 'bg-white';
};

// Legacy function for backward compatibility - converts to inline styles
export const getBackgroundStyle = (customBackgroundColor?: BackgroundColorInput): { backgroundColor: string } => {
  if (!customBackgroundColor) return { backgroundColor: '#ffffff' };

  // Handle string colors (new dropdown format or hex strings)
  if (typeof customBackgroundColor === 'string') {
    // Strip invisible/stega chars before using the value
    const cleanString = customBackgroundColor.replace(INVISIBLES, '').trim();

    // If it's a Tailwind class, don't return inline style
    if (cleanString.startsWith('bg-')) {
      return { backgroundColor: '' };
    }

    // Clean the hex color by removing any non-hex characters and invisible characters
    const cleanHex = cleanString.replace(/[^#0-9A-Fa-f]/g, '');

    // If it's a valid hex color, use it as inline style
    if (/^#[0-9A-Fa-f]{3,6}$/.test(cleanHex)) {
      return { backgroundColor: cleanHex };
    }
  }

  // Handle Sanity color picker objects (legacy format)
  if (typeof customBackgroundColor === 'object') {
    // Handle hex color
    if (customBackgroundColor.hex) {
      return { backgroundColor: customBackgroundColor.hex };
    }

    // Handle RGB color
    if (customBackgroundColor.rgb) {
      const { r, g, b, a } = customBackgroundColor.rgb;
      const alpha = a !== undefined ? a : 1;
      return { backgroundColor: `rgba(${r}, ${g}, ${b}, ${alpha})` };
    }

    // Handle HSL color
    if (customBackgroundColor.hsl) {
      const { h, s, l, a } = customBackgroundColor.hsl;
      const alpha = a !== undefined ? a : 1;
      return { backgroundColor: `hsla(${h}, ${s}%, ${l}%, ${alpha})` };
    }
  }

  return { backgroundColor: '#ffffff' };
};

export const getTextAlignment = (alignmentParam: string | undefined = 'left', isJustify: boolean = false) => {
  const alignment = stripInvisibles((alignmentParam || 'left').toLowerCase());

  // text alignment supports left | center | right
  const textClass =
    alignment === 'center' || alignment === 'middle'
      ? 'text-center'
      : alignment === 'right'
        ? 'text-right'
        : 'text-left';

  if (!isJustify) return textClass;

  // flex justify uses start | center | end (not left/right)
  const justifyClass =
    alignment === 'center' || alignment === 'middle'
      ? 'justify-center'
      : alignment === 'right'
        ? 'justify-end'
        : 'justify-start';

  return `${textClass} ${justifyClass}`;
};

export const getButtonVariant = (variant?: string | null): ButtonVariant => {
  if (!variant || typeof variant !== 'string') return 'primary';

  // normalize: remove invisible chars, trim, lowercase
  const normalized = stripInvisibles(variant).trim().toLowerCase();

  // keep only safe characters (letters, numbers, hyphen, underscore)
  const cleaned = normalized.replace(/[^a-z0-9-_]/g, '');

  // direct match
  const valid: readonly ButtonVariant[] = ['primary', 'secondary', 'tertiary', 'text'];

  if ((valid as readonly string[]).includes(cleaned)) {
    return cleaned as ButtonVariant;
  }

  // try to find a valid variant contained in the cleaned string (e.g. "primary-outline" -> "primary")
  const contained = valid.find((v) => cleaned === v || cleaned.includes(v));

  return (contained ?? 'primary') as ButtonVariant;
};

// Phone formatting functions
// Keep only an optional leading '+' and digits; drop spaces and any other characters
export const sanitizePhone = (raw: string): string => {
  if (!raw) return '';
  const s = String(raw);
  const hasPlus = s.trim().startsWith('+');
  const digits = s.replace(/[^0-9+]/g, '').replace(/^\+/, '');
  return (hasPlus ? '+' : '') + digits;
};

// Format to: '+64 4XX XXX XXX' or '04XX XXX XXX' while typing
export function formatPhoneAU(raw: string): string {
  const input = sanitizePhone(raw);
  if (!input) return '';
  const typer = new AsYouType('AU');
  const formatted = typer.input(input);
  // Ensure no parentheses are present, keep spaces as per AU formatting
  return formatted.replace(/[()]/g, '');
}

export function isValidPhoneAU(raw: string): boolean {
  if (!raw) return false;
  const s = sanitizePhone(raw).replace(/\s+/g, '');
  // International: +61 followed by exactly 10 digits, starting with 4 (mobile) or 2/3/7/8 (landline)
  if (s.startsWith('+')) {
    if (!s.startsWith('+61')) return false;
    const rest = s.slice(3);
    if (!/^\d{9}$/.test(rest)) return false;
    return rest.startsWith('4') || /^[2378]/.test(rest[0] ?? '');
  }
  // National: exactly 10 digits starting with 04 (mobile) or 0[2378] (landline)
  if (!/^\d{10}$/.test(s)) return false;
  if (s.startsWith('04')) return true;
  return s.startsWith('0') && /^[2378]/.test(s[1] ?? '');
}

export function trimToMaxLengthAU(raw: string): string {
  const s = sanitizePhone(raw);
  if (!s) return '';
  const digits = s.replace(/^\+/, '');
  // Keep up to 11 digits for international (+61 + 9 digits) or 10 for national
  const limited = s.startsWith('+') ? digits.slice(0, 11) : digits.slice(0, 10);
  return (s.startsWith('+') ? '+' : '') + limited;
}

/**
 * Scroll smoothly to the element with the given class name.
 * @param param0 fromId The ID of the element to scroll from
 * @param param1 toClassName The class name of the element to scroll to
 * @example
 * handleScrollToElement({ fromId: 'top-section', toClassName: '.target-section' });
 */
export const handleScrollToElement = ({
  fromId,
  toClassName = '.hero-banner-container',
}: {
  fromId: string;
  toClassName?: string;
}) => {
  setTimeout(() => {
    requestAnimationFrame(() => {
      const hero = document.querySelector<HTMLElement>(toClassName);
      if (hero) {
        const y = hero.getBoundingClientRect().bottom + window.pageYOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        return;
      }
      const el = document.getElementById(fromId);
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    });
  }, 100);
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
